import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiBellLight, PiBriefcaseLight, PiCheckCircleLight, PiInfoLight, PiCircleNotchLight, PiArrowCounterClockwiseLight, PiFloppyDiskLight, PiMagnifyingGlassLight, PiGearLight, PiShieldCheckLight, PiSlidersHorizontalLight, PiTargetLight, PiUsersLight, PiXLight, PiLightningLight } from 'react-icons/pi';
import useJobStore from '../../store/jobStore';

const bandDefinitions = [
  { value: 'A', label: 'Band A', detail: 'Strongest fit' },
  { value: 'B', label: 'Band B', detail: 'Good fit' },
  { value: 'C', label: 'Band C', detail: 'Broader review pool' },
  { value: 'D', label: 'Band D', detail: 'Low fit, useful for audits' }
];

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';
const textareaClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const cloneConfig = (config) => JSON.parse(JSON.stringify(config || {}));

const asArray = (value) => (Array.isArray(value) ? value : []);

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/[_.-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
);

const normalizeNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return numeric;
};

const infoHintWidth = 352;
const infoHintMargin = 12;

const dimensionHelpCopy = {
  skills: {
    meaning: 'This controls how much the match score cares about whether a candidate can do the work described in the job.',
    evidence: 'It compares required and preferred skills after they are normalized to the platform skill taxonomy, so equivalent wording can still be recognized.',
    guidance: 'Use a higher weight when specific tools, certifications, methods, or technical capabilities are required on day one. Use a lower weight when transferable skills are acceptable and you plan to train.'
  },
  experience: {
    meaning: 'This controls how much the score values the candidate having handled similar scope before.',
    evidence: 'It looks at seniority, years of experience, role responsibilities, industry context, stakeholder exposure, and structured work-history evidence.',
    guidance: 'Use a higher weight for roles where judgment, ownership level, regulated work, or domain familiarity matters. Use a lower weight for entry-level roles or roles where aptitude matters more than a matching background.'
  },
  education: {
    meaning: 'This controls how much formal education contributes to the ranking.',
    evidence: 'It considers degree level, field of study, institution context, GPA or classification where available, transcripts, and confidence in the education evidence.',
    guidance: 'Use a higher weight for licensed, academic, graduate, or compliance-heavy roles. Use a lower weight when portfolios, skills, or practical experience should carry more weight than credentials.'
  },
  micro_points: {
    meaning: 'This controls the influence of detailed evidence signals that sit underneath the broad score.',
    evidence: 'Examples include portfolio strength, research depth, language use, practical delivery signals, work readiness, and other role-specific indicators.',
    guidance: 'Use a higher weight when the small details should separate strong candidates from similar ones. Use a lower weight when you want the main dimensions, such as skills and experience, to drive most of the ranking.'
  },
  location: {
    meaning: 'This controls how much location and work-arrangement fit affect the match.',
    evidence: 'It can reflect location, remote or hybrid readiness, relocation willingness, timezone fit, region fit, and work authorization signals.',
    guidance: 'Use a higher weight when the role requires a specific location, onsite availability, or authorization status. Use a lower weight for flexible, fully remote, or location-agnostic roles.'
  },
  salary: {
    meaning: 'This controls how much compensation alignment affects the candidate ranking.',
    evidence: 'It compares candidate expectations with the pay range configured on the job when that information is available.',
    guidance: 'Use a higher weight when budget fit is strict or you need to avoid late-stage compensation mismatch. Use a lower weight when compensation is flexible, negotiable, or handled later in the process.'
  }
};

const getInfoHintPosition = (buttonElement, panelElement, desiredWidth = infoHintWidth) => {
  if (!buttonElement || typeof window === 'undefined') {
    return { top: infoHintMargin, left: infoHintMargin, width: desiredWidth };
  }

  const rect = buttonElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth || infoHintWidth;
  const viewportHeight = window.innerHeight || 640;
  const availableWidth = Math.max(180, viewportWidth - (infoHintMargin * 2));
  const width = Math.min(desiredWidth, availableWidth);
  const panelHeight = panelElement?.offsetHeight || 260;

  let left = rect.left + (rect.width / 2) - (width / 2);
  left = Math.max(infoHintMargin, Math.min(left, viewportWidth - width - infoHintMargin));

  const belowTop = rect.bottom + 10;
  const aboveTop = rect.top - panelHeight - 10;
  const top = belowTop + panelHeight <= viewportHeight - infoHintMargin
    ? belowTop
    : Math.max(infoHintMargin, aboveTop);

  return { top, left, width };
};

const InfoHint = ({ title, children, width = infoHintWidth }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: infoHintMargin, left: infoHintMargin, width });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const hintId = useId();

  const updatePosition = useCallback(() => {
    setPosition(getInfoHintPosition(buttonRef.current, panelRef.current, width));
  }, [width]);

  const handleToggle = () => {
    if (!open) updatePosition();
    setOpen(current => !current);
  };

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    const animationFrame = window.requestAnimationFrame(updatePosition);

    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  return (
    <span className="inline-flex">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? hintId : undefined}
        aria-haspopup="dialog"
        aria-label={title ? `Show help for ${title}` : 'Show explanation'}
        onClick={handleToggle}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border bg-white transition ${open ? 'border-sky-300 text-sky-700 ring-2 ring-sky-100' : 'border-slate-200 text-slate-400 hover:border-sky-200 hover:text-sky-700'}`}
      >
        <PiInfoLight size={12} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          id={hintId}
          role="dialog"
          aria-label={title ? `${title} help` : 'Help'}
          className="fixed z-[80] max-h-[72vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 text-left text-sm leading-6 text-slate-600 shadow-[0_24px_60px_rgba(15,23,42,0.20)]"
          style={{ top: position.top, left: position.left, width: position.width }}
        >
          <div className="mb-2 flex items-start justify-between gap-3">
            {title && <span className="block font-medium leading-5 text-[#0b1f3a]">{title}</span>}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-50 hover:text-slate-700"
              aria-label="Close help"
            >
              <PiXLight size={12} />
            </button>
          </div>
          <div className="space-y-2">{children}</div>
        </div>,
        document.body
      )}
    </span>
  );
};

const DimensionWeightsHelp = ({ activeTotal }) => (
  <>
    <p>Use these weights to tell Sanspath what should matter most when ranking candidates for your jobs.</p>
    <p>The system normalizes the active dimensions during scoring, so the current active total of {activeTotal.toFixed(0)}% still behaves predictably. Raising one dimension makes it more influential compared with the other active dimensions.</p>
    <p>Start by weighting your true deal-breakers more heavily, then use categories and micro-points when you need more granular control.</p>
  </>
);

const DimensionHelp = ({ dimension }) => {
  const copy = dimensionHelpCopy[dimension.key] || {
    meaning: dimension.description || `This controls how much ${dimension.label || 'this dimension'} affects candidate ranking.`,
    evidence: 'The matcher uses available candidate and job evidence for this dimension when calculating the score.',
    guidance: 'Use a higher weight when this should strongly influence who appears at the top. Use a lower weight when it should only support the wider hiring decision.'
  };
  const weightText = dimension.enabled === false
    ? 'This dimension is currently off, so it will not contribute to candidate scores.'
    : `Current weight: ${normalizeNumber(dimension.weight, dimension.defaultWeight || 0)}%. This is compared with the other active dimensions when the final match score is calculated.`;

  return (
    <>
      <p><span className="font-medium text-[#0b1f3a]">What it means: </span>{copy.meaning}</p>
      <p><span className="font-medium text-[#0b1f3a]">What it checks: </span>{copy.evidence}</p>
      <p><span className="font-medium text-[#0b1f3a]">How to use it: </span>{copy.guidance}</p>
      <p className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500">{weightText}</p>
    </>
  );
};

const normalizeLookup = (value) => String(value || '').trim().toLowerCase().replace(/[_.\s-]+/g, ' ');

const getCategoryName = (category) => (
  category?.label || category?.name || (category?.key ? labelize(category.key) : 'Uncategorized')
);

const getMicroPointName = (point) => (
  point?.displayName || point?.label || (point?.key ? labelize(point.key) : 'Unnamed micro-point')
);

const pointBelongsToCategory = (point, category) => {
  const pointCategory = normalizeLookup(point?.category);
  if (!pointCategory || !category) return false;
  return [category.key, category.label, category.name]
    .filter(Boolean)
    .some(value => normalizeLookup(value) === pointCategory);
};

const getCategoryMicroPoints = (category, microPoints) => (
  asArray(microPoints).filter(point => pointBelongsToCategory(point, category))
);

const getPointCategory = (point, categories) => (
  asArray(categories).find(category => pointBelongsToCategory(point, category))
);

const HelpFact = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-medium text-[#0b1f3a]">{value}</span>
  </div>
);

const CategoryControlsHelp = ({ categories, microPoints }) => {
  const enabledCategories = asArray(categories).filter(category => category.enabled !== false).length;
  const enabledMicroPoints = asArray(microPoints).filter(point => point.enabled !== false).length;

  return (
    <>
      <p><span className="font-medium text-[#0b1f3a]">What categories are: </span>Categories are broad matching policy groups. They keep related micro-points together so you can adjust a whole evidence area without editing every detailed signal one by one.</p>
      <p><span className="font-medium text-[#0b1f3a]">How they work: </span>Turning a category off removes the related micro-point requirements from matching. Category influence acts as a broad weight for the group, while an individual micro-point weight remains the more specific override.</p>
      <div className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
        <div className="mb-2 font-medium text-[#0b1f3a]">Configured categories</div>
        <div className="space-y-1.5">
          {asArray(categories).map(category => {
            const relatedMicroPoints = getCategoryMicroPoints(category, microPoints);
            return (
              <div key={category.key || getCategoryName(category)} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate">{getCategoryName(category)}</span>
                <span className="shrink-0 text-slate-500">{relatedMicroPoints.length} micro-points</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="rounded-md bg-sky-50 px-3 py-2 text-xs leading-5 text-sky-800">Current setup: {enabledCategories} of {asArray(categories).length} categories are on, and {enabledMicroPoints} micro-points are enabled.</p>
    </>
  );
};

const CategoryHelp = ({ category, microPoints }) => {
  const categoryName = getCategoryName(category);
  const relatedMicroPoints = getCategoryMicroPoints(category, microPoints);
  const enabledRelatedMicroPoints = relatedMicroPoints.filter(point => point.enabled !== false).length;
  const categoryWeight = normalizeNumber(category?.weight, category?.defaultWeight || 0);
  const statusText = category?.enabled === false
    ? 'This category is currently off, so its related micro-point requirements will not contribute until it is enabled again.'
    : `Current influence: ${categoryWeight}%. This scales the category as a group when related micro-points do not have a more specific employer weight.`;

  return (
    <>
      <p><span className="font-medium text-[#0b1f3a]">What it is: </span>{categoryName} is a category, meaning it is a broad evidence area used to group related micro-points under one policy control.</p>
      <p><span className="font-medium text-[#0b1f3a]">What it covers: </span>{category?.description || 'This category does not have a saved description yet. Use the editable description field to define what evidence belongs here.'}</p>
      <p><span className="font-medium text-[#0b1f3a]">How to use it: </span>Raise the influence when this whole evidence area should matter more across jobs. Lower it when the category should support the decision but not dominate candidate ranking.</p>
      <div className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
        <div className="mb-2 font-medium text-[#0b1f3a]">Micro-points in this category</div>
        {relatedMicroPoints.length ? (
          <div className="space-y-1.5">
            {relatedMicroPoints.map(point => (
              <div key={point.key || getMicroPointName(point)} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate">{getMicroPointName(point)}</span>
                <span className={`shrink-0 rounded px-1.5 py-0.5 ${point.enabled === false ? 'bg-slate-100 text-slate-500' : 'bg-sky-100 text-sky-700'}`}>
                  {point.enabled === false ? 'Off' : 'On'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p>No configured micro-points currently reference this category.</p>
        )}
      </div>
      <div className="rounded-md bg-sky-50 px-3 py-2 text-xs leading-5 text-sky-800">
        <HelpFact label="Status" value={category?.enabled === false ? 'Off' : 'On'} />
        <HelpFact label="Enabled micro-points" value={`${enabledRelatedMicroPoints} of ${relatedMicroPoints.length}`} />
        <HelpFact label="Influence" value={`${categoryWeight}%`} />
      </div>
      <p className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500">{statusText}</p>
    </>
  );
};

const MicroPointPolicyHelp = ({ microPoints, categories, visibleCount }) => {
  const enabledMicroPoints = asArray(microPoints).filter(point => point.enabled !== false).length;
  const mandatoryMicroPoints = asArray(microPoints).filter(point => point.enabled !== false && point.mandatory).length;
  const sampleMicroPoints = asArray(microPoints).slice(0, 12);

  return (
    <>
      <p><span className="font-medium text-[#0b1f3a]">What micro-points are: </span>Micro-points are the fine-grained evidence checks underneath the broader scoring dimensions and categories. They capture specific signals that can separate candidates who otherwise look similar.</p>
      <p><span className="font-medium text-[#0b1f3a]">How they are used: </span>Enabled micro-points can be attached to extracted job requirements and read during deterministic matching. Weight changes how strongly the point contributes; mandatory points create stronger gaps when required evidence is missing.</p>
      <p><span className="font-medium text-[#0b1f3a]">How to tune them: </span>Use search and category filters to find the point you want, then adjust its description, weight, mandatory flag, minimum score, or employer note. Use category influence for broad policy changes before fine tuning individual points.</p>
      <div className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
        <div className="mb-2 font-medium text-[#0b1f3a]">Configured micro-points</div>
        {sampleMicroPoints.length ? (
          <div className="space-y-1.5">
            {sampleMicroPoints.map(point => {
              const category = getPointCategory(point, categories);
              return (
                <div key={point.key || getMicroPointName(point)} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate">{getMicroPointName(point)}</span>
                  <span className="shrink-0 text-slate-500">{category ? getCategoryName(category) : labelize(point.category)}</span>
                </div>
              );
            })}
            {asArray(microPoints).length > sampleMicroPoints.length && (
              <div className="text-slate-500">+ {asArray(microPoints).length - sampleMicroPoints.length} more in the editable list below</div>
            )}
          </div>
        ) : (
          <p>No micro-points are currently configured.</p>
        )}
      </div>
      <div className="rounded-md bg-sky-50 px-3 py-2 text-xs leading-5 text-sky-800">
        <HelpFact label="Visible now" value={`${visibleCount ?? asArray(microPoints).length} of ${asArray(microPoints).length}`} />
        <HelpFact label="Enabled points" value={`${enabledMicroPoints}`} />
        <HelpFact label="Mandatory points" value={`${mandatoryMicroPoints}`} />
      </div>
    </>
  );
};

const MicroPointHelp = ({ point, category }) => {
  const pointName = getMicroPointName(point);
  const categoryName = category ? getCategoryName(category) : labelize(point?.category);
  const pointWeight = normalizeNumber(point?.weight, point?.defaultWeight || 0);
  const statusText = point?.enabled === false
    ? 'This micro-point is currently off, so it will not be used as a matching requirement until it is enabled again.'
    : `Current weight: ${pointWeight}%. This is the point-level influence used for this specific signal.`;
  const minimumScore = point?.minimumScore === null || point?.minimumScore === undefined || point?.minimumScore === ''
    ? 'Auto'
    : `${point.minimumScore}%`;

  return (
    <>
      <p><span className="font-medium text-[#0b1f3a]">What it is: </span>{pointName} is a micro-point, meaning a specific evidence signal used underneath categories and scoring dimensions.</p>
      <p><span className="font-medium text-[#0b1f3a]">What it checks: </span>{point?.description || 'This micro-point does not have a saved description yet. Add one so reviewers know what candidate evidence should satisfy it.'}</p>
      <p><span className="font-medium text-[#0b1f3a]">Where it belongs: </span>This point sits in the {categoryName} category{point?.subcategory ? `, under ${labelize(point.subcategory)}` : ''}. The category handles broad policy, while this point controls the specific signal.</p>
      <p><span className="font-medium text-[#0b1f3a]">How it affects matching: </span>When enabled, the matcher can use this point while extracting job requirements and scoring candidate evidence. Mandatory points are treated as stronger gaps when the required evidence is missing.</p>
      <div className="rounded-md bg-sky-50 px-3 py-2 text-xs leading-5 text-sky-800">
        <HelpFact label="Status" value={point?.enabled === false ? 'Off' : 'On'} />
        <HelpFact label="Category" value={categoryName} />
        <HelpFact label="Weight" value={`${pointWeight}%`} />
        <HelpFact label="Default weight" value={`${normalizeNumber(point?.defaultWeight, pointWeight)}%`} />
        <HelpFact label="Mandatory" value={point?.mandatory ? 'Yes' : 'No'} />
        <HelpFact label="Minimum score" value={minimumScore} />
        {point?.scoringMethod && <HelpFact label="Scoring method" value={labelize(point.scoringMethod)} />}
      </div>
      <p className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500">{statusText}</p>
    </>
  );
};

const Panel = ({ icon: Icon, title, subtitle, children, action }) => (
  <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-4 sm:p-5">
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex shrink-0 items-center justify-center text-sky-600">
          <Icon size={30} />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{title}</h2>
          {subtitle && <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const Metric = ({ icon: Icon, label, value, detail }) => (
  <div className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
    <div className="mb-3 flex items-center justify-center text-sky-600">
      <Icon size={30} />
    </div>
    <div className="font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
    <div className="mt-2 text-sm font-medium text-slate-700">{label}</div>
    <div className="mt-1 text-xs leading-5 text-slate-500">{detail}</div>
  </div>
);

const Toggle = ({ checked, onChange, label, description, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`flex w-full items-start justify-between gap-4 rounded-lg border p-4 text-left transition disabled:opacity-60 ${checked ? 'border-sky-200 bg-sky-50/70' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
  >
    <span>
      <span className="block text-sm font-medium text-[#0b1f3a]">{label}</span>
      <span className="mt-1 block text-xs leading-5 text-slate-500">{description}</span>
    </span>
    <span className={`mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition ${checked ? 'bg-sky-600' : 'bg-slate-300'}`}>
      <span className={`h-4 w-4 rounded-full bg-white transition ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </span>
  </button>
);

const WeightInput = ({ value, onChange, min = 0, max = 100, suffix = '%', disabled }) => {
  const currentValue = normalizeNumber(value, 0);
  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={min}
        max={max}
        value={currentValue}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
        className="min-w-0 flex-1 accent-sky-600 disabled:opacity-40"
      />
      <div className="relative w-24 shrink-0">
        <input
          type="number"
          min={min}
          max={max}
          value={currentValue}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
          className={`${inputClass} w-full pr-8 disabled:bg-slate-50 disabled:text-slate-400`}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>
      </div>
    </div>
  );
};

const SmallToggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition disabled:opacity-50 ${checked ? 'bg-sky-600' : 'bg-slate-300'}`}
  >
    <span className={`h-5 w-5 rounded-full bg-white transition ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const AdvancedSettingsModal = ({
  open,
  draft,
  saving,
  onClose,
  onSave,
  onReset,
  onDraftChange
}) => {
  const [tab, setTab] = useState('dimensions');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    if (open) {
      setTab('dimensions');
      setSearch('');
      setCategoryFilter('all');
    }
  }, [open]);

  if (!open) return null;

  const updatePreference = (field, value) => {
    onDraftChange(prev => ({
      ...prev,
      preferences: { ...(prev.preferences || {}), [field]: value }
    }));
  };

  const updateListItem = (listName, key, patch) => {
    onDraftChange(prev => ({
      ...prev,
      [listName]: asArray(prev[listName]).map(item => (
        item.key === key ? { ...item, ...patch } : item
      ))
    }));
  };

  const resetListToDefaults = (listName) => {
    onDraftChange(prev => ({
      ...prev,
      [listName]: cloneConfig(prev.defaults?.[listName] || [])
    }));
  };

  const dimensionTotal = asArray(draft.dimensionWeights)
    .filter(item => item.enabled !== false)
    .reduce((sum, item) => sum + normalizeNumber(item.weight, 0), 0);

  const categories = asArray(draft.categories);
  const microPoints = asArray(draft.microPoints);
  const categoryOptions = Array.from(new Set(microPoints.map(point => point.category).filter(Boolean))).sort();
  const filteredMicroPoints = microPoints.filter((point) => {
    const query = search.trim().toLowerCase();
    const matchesQuery = !query || [
      point.displayName,
      point.key,
      point.description,
      point.category,
      point.subcategory
    ].filter(Boolean).join(' ').toLowerCase().includes(query);
    const matchesCategory = categoryFilter === 'all' || point.category === categoryFilter;
    return matchesQuery && matchesCategory;
  });

  const tabs = [
    { id: 'dimensions', label: 'Dimensions', count: draft.dimensionWeights?.length || 0 },
    { id: 'categories', label: 'Categories', count: categories.length },
    { id: 'microPoints', label: 'Micro-points', count: microPoints.length },
    { id: 'preferences', label: 'Preferences', count: null }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-3 sm:p-6">
      <div className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-4 sm:p-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Advanced Settings</p>
            <h2 className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">Employer Matching Configuration</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Modify employer-specific dimensions, categories, micro-points, thresholds, and matching preferences. Defaults remain available for reset.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-[#0b1f3a]"
          >
            <PiXLight size={14} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          {tabs.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium transition ${tab === item.id ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500 hover:text-[#0b1f3a]'}`}
            >
              {item.label}
              {item.count !== null && <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">{item.count}</span>}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {tab === 'dimensions' && (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#0b1f3a]">
                    Dimension weights
                    <InfoHint title="Dimension weights">
                      <DimensionWeightsHelp activeTotal={dimensionTotal} />
                    </InfoHint>
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">Active total: {dimensionTotal.toFixed(0)}%. Backend normalization keeps scoring stable if the total is not exactly 100%.</div>
                </div>
                <button
                  type="button"
                  onClick={() => resetListToDefaults('dimensionWeights')}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  <PiArrowCounterClockwiseLight size={12} /> Reset dimensions
                </button>
              </div>

              <div className="space-y-3">
                {asArray(draft.dimensionWeights).map(item => (
                  <div key={item.key} className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#0b1f3a]">{item.label}</span>
                          <InfoHint title={item.label}>
                            <DimensionHelp dimension={item} />
                          </InfoHint>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-slate-500">{item.description}</p>
                      </div>
                      <SmallToggle checked={item.enabled !== false} onChange={(value) => updateListItem('dimensionWeights', item.key, { enabled: value })} />
                    </div>
                    <WeightInput
                      value={item.weight}
                      disabled={item.enabled === false}
                      onChange={(value) => updateListItem('dimensionWeights', item.key, { weight: value })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'categories' && (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#0b1f3a]">
                    Category controls
                    <InfoHint title="Category controls" width={480}>
                      <CategoryControlsHelp categories={categories} microPoints={microPoints} />
                    </InfoHint>
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">Use category influence for broad policy shifts, then fine tune individual micro-points.</div>
                </div>
                <button
                  type="button"
                  onClick={() => resetListToDefaults('categories')}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  <PiArrowCounterClockwiseLight size={12} /> Reset categories
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {categories.map(item => (
                  <div key={item.key} className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <input
                            value={item.label || ''}
                            onChange={(event) => updateListItem('categories', item.key, { label: event.target.value })}
                            className="h-8 min-w-0 rounded-md border border-transparent bg-transparent px-0 text-sm font-medium text-[#0b1f3a] outline-none transition focus:border-slate-200 focus:bg-white focus:px-2"
                          />
                          <InfoHint title={item.label || labelize(item.key)} width={480}>
                            <CategoryHelp category={item} microPoints={microPoints} />
                          </InfoHint>
                        </div>
                        <textarea
                          rows={2}
                          value={item.description || ''}
                          onChange={(event) => updateListItem('categories', item.key, { description: event.target.value })}
                          className="mt-2 w-full resize-none rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs leading-5 text-slate-600 outline-none focus:border-sky-300 focus:bg-white"
                        />
                      </div>
                      <SmallToggle checked={item.enabled !== false} onChange={(value) => updateListItem('categories', item.key, { enabled: value })} />
                    </div>
                    <div className="mb-2 flex justify-between text-xs text-slate-500">
                      <span>Influence</span>
                      <span>{item.microPointCount || 0} micro-points</span>
                    </div>
                    <WeightInput
                      value={item.weight}
                      disabled={item.enabled === false}
                      onChange={(value) => updateListItem('categories', item.key, { weight: value })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'microPoints' && (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#0b1f3a]">
                    Micro-point policy
                    <InfoHint title="Micro-points" width={480}>
                      <MicroPointPolicyHelp categories={categories} microPoints={microPoints} visibleCount={filteredMicroPoints.length} />
                    </InfoHint>
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">{filteredMicroPoints.length} visible of {microPoints.length} configured micro-points.</div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="relative">
                    <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
                    <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search micro-points" className={`${inputClass} w-full pl-9 sm:w-64`} />
                  </div>
                  <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} className={inputClass}>
                    <option value="all">All categories</option>
                    {categoryOptions.map(category => <option key={category} value={category}>{labelize(category)}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => resetListToDefaults('microPoints')}
                    className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                  >
                    <PiArrowCounterClockwiseLight size={12} /> Reset
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {filteredMicroPoints.map(point => {
                  const pointCategory = getPointCategory(point, categories);
                  return (
                    <div key={point.key} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(300px,0.85fr)]">
                        <div className="min-w-0">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <input
                                  value={point.displayName || ''}
                                  onChange={(event) => updateListItem('microPoints', point.key, { displayName: event.target.value })}
                                  className="h-8 min-w-0 rounded-md border border-transparent bg-transparent px-0 text-sm font-medium text-[#0b1f3a] outline-none transition focus:border-slate-200 focus:bg-white focus:px-2"
                                />
                                <InfoHint title={point.displayName || point.key} width={480}>
                                  <MicroPointHelp point={point} category={pointCategory} />
                                </InfoHint>
                              </div>
                              <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                                <span>{point.key}</span>
                                <span>{labelize(point.category)}</span>
                                {point.subcategory && <span>{labelize(point.subcategory)}</span>}
                                {point.scoringMethod && <span>{labelize(point.scoringMethod)}</span>}
                              </div>
                            </div>
                            <SmallToggle checked={point.enabled !== false} onChange={(value) => updateListItem('microPoints', point.key, { enabled: value })} />
                          </div>

                          <textarea
                            rows={3}
                            value={point.description || ''}
                            onChange={(event) => updateListItem('microPoints', point.key, { description: event.target.value })}
                            className={textareaClass}
                          />
                        </div>

                      <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div>
                          <div className="mb-2 flex items-center justify-between gap-3">
                            <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Weight</span>
                            <span className="text-xs text-slate-500">Default {point.defaultWeight}%</span>
                          </div>
                          <WeightInput
                            value={point.weight}
                            disabled={point.enabled === false}
                            onChange={(value) => updateListItem('microPoints', point.key, { weight: value })}
                          />
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm font-medium text-slate-700">
                            <input
                              type="checkbox"
                              checked={Boolean(point.mandatory)}
                              disabled={point.enabled === false}
                              onChange={(event) => updateListItem('microPoints', point.key, { mandatory: event.target.checked })}
                              className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                            />
                            Mandatory
                          </label>
                          <div>
                            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Minimum score</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={point.minimumScore ?? ''}
                              disabled={point.enabled === false}
                              onChange={(event) => updateListItem('microPoints', point.key, { minimumScore: event.target.value === '' ? null : Number(event.target.value) })}
                              className={`${inputClass} w-full disabled:bg-slate-100 disabled:text-slate-400`}
                              placeholder="Auto"
                            />
                          </div>
                        </div>

                        <textarea
                          rows={2}
                          value={point.employerNotes || ''}
                          onChange={(event) => updateListItem('microPoints', point.key, { employerNotes: event.target.value })}
                          className={textareaClass}
                          placeholder="Employer note"
                        />
                      </div>
                    </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'preferences' && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <Toggle
                checked={Boolean(draft.preferences?.enableImmediateMatching)}
                onChange={(value) => updatePreference('enableImmediateMatching', value)}
                label="Enable immediate matching"
                description="Run candidate matching when jobs are published or manually run."
              />
              <Toggle
                checked={Boolean(draft.preferences?.matchingNotifications)}
                onChange={(value) => updatePreference('matchingNotifications', value)}
                label="Matching notifications"
                description="Allow backend notifications for matching and employer interest events."
              />
              <Toggle
                checked={Boolean(draft.preferences?.autoContactTopMatches)}
                onChange={(value) => updatePreference('autoContactTopMatches', value)}
                label="Auto-contact top matches"
                description="Persist this automation preference backend workflows that support it."
              />
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-[#0b1f3a]">
                  Preferred bands
                  <InfoHint title="Preferred bands">
                    These bands become the default employer review pool on jobs and matches screens.
                  </InfoHint>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {bandDefinitions.map(band => {
                    const selected = asArray(draft.preferences?.preferredBands).includes(band.value);
                    return (
                      <button
                        key={band.value}
                        type="button"
                        onClick={() => {
                          const current = asArray(draft.preferences?.preferredBands);
                          const next = selected
                            ? current.filter(item => item !== band.value)
                            : [...current, band.value].sort();
                          updatePreference('preferredBands', next.length ? next : current);
                        }}
                        className={`rounded-lg border p-3 text-left transition ${selected ? 'border-sky-200 bg-sky-50 text-[#0b1f3a]' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                      >
                        <span className="block text-sm font-medium">{band.label}</span>
                        <span className="mt-1 block text-xs text-slate-500">{band.detail}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-[#0b1f3a]">Immediate match limit</span>
                  <span className="text-sm font-medium text-sky-700">{draft.preferences?.immediateMatchLimit || 10}</span>
                </div>
                <WeightInput
                  value={draft.preferences?.immediateMatchLimit || 10}
                  min={1}
                  max={50}
                  suffix=""
                  onChange={(value) => updatePreference('immediateMatchLimit', value)}
                />
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-[#0b1f3a]">Minimum score threshold</span>
                  <span className="text-sm font-medium text-sky-700">{draft.preferences?.minimumScoreThreshold || 50}%</span>
                </div>
                <WeightInput
                  value={draft.preferences?.minimumScoreThreshold || 50}
                  onChange={(value) => updatePreference('minimumScoreThreshold', value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onReset}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
          >
            <PiArrowCounterClockwiseLight size={13} /> Reset all to defaults
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-50"
            >
              {saving ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiFloppyDiskLight size={13} />}
              Save advanced settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfigPage = () => {
  const {
    matchingPreferences,
    matchingConfiguration,
    jobAnalytics,
    configurationLoading,
    analyticsLoading,
    error,
    updateMatchingPreferences,
    fetchMatchingConfiguration,
    updateMatchingConfiguration,
    fetchJobAnalytics,
    clearError
  } = useJobStore();

  const [form, setForm] = useState(matchingPreferences);
  const [draftConfig, setDraftConfig] = useState(cloneConfig(matchingConfiguration));
  const [saving, setSaving] = useState(false);
  const [savingAdvanced, setSavingAdvanced] = useState(false);
  const [timeframe, setTimeframe] = useState('30d');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [configResult, analyticsResult] = await Promise.all([
        fetchMatchingConfiguration(),
        fetchJobAnalytics({ timeframe })
      ]);

      if (configResult.success) {
        setDraftConfig(cloneConfig(configResult.configuration));
        setForm(configResult.configuration.preferences);
      }

      if (!analyticsResult.success) {
        toast.error(analyticsResult.error, { id: 'config-job-analytics-error' });
      }
    };
    load();
  }, [fetchJobAnalytics, fetchMatchingConfiguration, timeframe]);

  useEffect(() => {
    setForm(matchingPreferences);
  }, [matchingPreferences]);

  useEffect(() => {
    setDraftConfig(cloneConfig(matchingConfiguration));
  }, [matchingConfiguration]);

  const analytics = jobAnalytics || {};
  const jobStats = analytics.jobs || {};
  const matchStats = analytics.matches || {};
  const offerStats = analytics.offers || {};

  const dimensionWeights = asArray(matchingConfiguration.dimensionWeights);
  const categories = asArray(matchingConfiguration.categories);
  const microPoints = asArray(matchingConfiguration.microPoints);
  const enabledMicroPoints = microPoints.filter(item => item.enabled !== false).length;

  const hasChanges = useMemo(() => JSON.stringify(form) !== JSON.stringify(matchingPreferences), [form, matchingPreferences]);

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleBand = (band) => {
    setForm(prev => {
      const current = Array.isArray(prev.preferredBands) ? prev.preferredBands : [];
      const next = current.includes(band)
        ? current.filter(item => item !== band)
        : [...current, band].sort();
      return { ...prev, preferredBands: next.length ? next : current };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updateMatchingPreferences({
      ...form,
      immediateMatchLimit: Number(form.immediateMatchLimit) || 10,
      minimumScoreThreshold: Number(form.minimumScoreThreshold) || 50,
      preferredBands: form.preferredBands?.length ? form.preferredBands : ['A', 'B']
    });
    if (result.success) toast.success('Matching preferences saved.');
    else toast.error(result.error);
    setSaving(false);
  };

  const handleAdvancedSave = async () => {
    setSavingAdvanced(true);
    const result = await updateMatchingConfiguration(draftConfig);
    if (result.success) {
      toast.success('Advanced matching configuration saved.');
      setAdvancedOpen(false);
      setDraftConfig(cloneConfig(result.configuration));
      setForm(result.configuration.preferences);
    } else {
      toast.error(result.error);
    }
    setSavingAdvanced(false);
  };

  const handleAdvancedReset = async () => {
    setSavingAdvanced(true);
    const result = await updateMatchingConfiguration({ resetToDefaults: true });
    if (result.success) {
      toast.success('Matching configuration reset to defaults.');
      setDraftConfig(cloneConfig(result.configuration));
      setForm(result.configuration.preferences);
    } else {
      toast.error(result.error);
    }
    setSavingAdvanced(false);
  };

  const configBusy = configurationLoading || analyticsLoading;

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Configuration</p>
            <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Matching Configuration</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Configure employer-owned matching behavior, scoring weights, categories, micro-points, and default review policy.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setDraftConfig(cloneConfig(matchingConfiguration));
                setAdvancedOpen(true);
              }}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              <PiSlidersHorizontalLight size={13} /> Advanced settings
            </button>
            <button
              onClick={() => setForm(matchingPreferences)}
              disabled={!hasChanges || saving}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-50"
            >
              {saving ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiFloppyDiskLight size={13} />}
              Save changes
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start justify-between gap-4 rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <div className="flex gap-2">
              <PiWarningCircleLight size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
            <button onClick={clearError} className="text-xs font-medium text-rose-700 hover:text-rose-900">Dismiss</button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Metric icon={PiBriefcaseLight} label="Total jobs" value={jobStats.total || 0} detail={`${jobStats.active || 0} active, ${jobStats.draft || 0} draft`} />
          <Metric icon={PiUsersLight} label="Total matches" value={matchStats.total || 0} detail={`${matchStats.createdInTimeframe || 0} in selected timeframe`} />
          <Metric icon={PiTargetLight} label="Average score" value={`${matchStats.averageScore || 0}%`} detail="Across matches" />
          <Metric icon={PiCheckCircleLight} label="Offer acceptance" value={`${offerStats.acceptanceRate || 0}%`} detail={`${offerStats.accepted || 0} accepted offers`} />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="space-y-6 xl:col-span-2">
            <Panel
              icon={PiSlidersHorizontalLight}
              title="Immediate Matching"
              subtitle="Controls used by create, publish, manual matching, and matches review flows."
              action={configBusy && <span className="inline-flex items-center gap-2 text-xs text-slate-500"><PiCircleNotchLight className="animate-spin" size={12} /> Loading</span>}
            >
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Toggle
                  checked={Boolean(form.enableImmediateMatching)}
                  onChange={(value) => updateForm('enableImmediateMatching', value)}
                  label="Enable immediate matching"
                  description="Run candidate matching when jobs are published or manually run."
                />
                <Toggle
                  checked={Boolean(form.matchingNotifications)}
                  onChange={(value) => updateForm('matchingNotifications', value)}
                  label="Matching notifications"
                  description="Allow backend notifications for matching and employer interest events."
                />
                <Toggle
                  checked={Boolean(form.autoContactTopMatches)}
                  onChange={(value) => updateForm('autoContactTopMatches', value)}
                  label="Auto-contact top matches"
                  description="Stored preference for future automation. Manual review remains available."
                />
              </div>

              <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="text-sm font-medium text-[#0b1f3a]">Immediate match limit</label>
                    <span className="text-sm font-medium text-sky-700">{form.immediateMatchLimit || 10}</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={form.immediateMatchLimit || 10}
                    onChange={(event) => updateForm('immediateMatchLimit', Number(event.target.value))}
                    className="w-full accent-sky-600"
                  />
                  <p className="mt-1 text-xs leading-5 text-slate-500">Maximum top matches returned in immediate matching surfaces.</p>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="text-sm font-medium text-[#0b1f3a]">Minimum score threshold</label>
                    <span className="text-sm font-medium text-sky-700">{form.minimumScoreThreshold || 50}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={form.minimumScoreThreshold || 50}
                    onChange={(event) => updateForm('minimumScoreThreshold', Number(event.target.value))}
                    className="w-full accent-sky-600"
                  />
                  <p className="mt-1 text-xs leading-5 text-slate-500">Candidates below this score are hidden from immediate match review.</p>
                </div>
              </div>
            </Panel>

            <Panel icon={PiTargetLight} title="Preferred Match Bands" subtitle="Bands determine the default review pool for employer matching.">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {bandDefinitions.map((band) => {
                  const selected = form.preferredBands?.includes(band.value);
                  return (
                    <button
                      key={band.value}
                      onClick={() => toggleBand(band.value)}
                      className={`rounded-lg border p-4 text-left transition ${selected ? 'border-sky-200 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-[#0b1f3a]">{band.label}</div>
                          <div className="mt-1 text-xs leading-5 text-slate-500">{band.detail}</div>
                        </div>
                        {selected ? <PiCheckCircleLight className="text-sky-700" size={14} /> : <span className="h-4 w-4 rounded-full border border-slate-300" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Panel>

            <Panel icon={PiShieldCheckLight} title="Scoring Dimensions" subtitle="Controls which evidence has the strongest influence when candidates are ranked.">
              <div className="space-y-3">
                {dimensionWeights.map((dimension) => (
                  <div key={dimension.key}>
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">{dimension.label}</span>
                        <InfoHint title={dimension.label}>
                          <DimensionHelp dimension={dimension} />
                        </InfoHint>
                      </div>
                      <span className="text-sm font-medium text-[#0b1f3a]">{dimension.enabled === false ? 'Off' : `${dimension.weight}%`}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-sky-600" style={{ width: `${dimension.enabled === false ? 0 : dimension.weight}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <div className="space-y-6">
            <Panel icon={PiGearLight} title="Analytics Window" subtitle="Use backend analytics to validate matching outcomes.">
              <select value={timeframe} onChange={(event) => setTimeframe(event.target.value)} className={`${inputClass} w-full`}>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-3"><span className="text-slate-500">Fill rate</span><span className="font-medium text-[#0b1f3a]">{jobStats.fillRate || 0}%</span></div>
                <div className="flex justify-between gap-3"><span className="text-slate-500">Mutual rate</span><span className="font-medium text-[#0b1f3a]">{matchStats.mutualRate || 0}%</span></div>
                <div className="flex justify-between gap-3"><span className="text-slate-500">Employer interested</span><span className="font-medium text-[#0b1f3a]">{matchStats.employerInterested || 0}</span></div>
                <div className="flex justify-between gap-3"><span className="text-slate-500">Candidate interested</span><span className="font-medium text-[#0b1f3a]">{matchStats.candidateInterested || 0}</span></div>
              </div>
            </Panel>

            <Panel icon={PiBellLight} title="Categories and Micro-points" subtitle="Employer-owned matching evidence controls.">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-2xl font-display font-normal text-[#0b1f3a]">{categories.length}</div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                    <span>Categories</span>
                    <InfoHint title="Categories" width={480}>
                      <CategoryControlsHelp categories={categories} microPoints={microPoints} />
                    </InfoHint>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-2xl font-display font-normal text-[#0b1f3a]">{enabledMicroPoints}</div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                    <span>Enabled points</span>
                    <InfoHint title="Micro-points" width={480}>
                      <MicroPointPolicyHelp categories={categories} microPoints={microPoints} visibleCount={microPoints.length} />
                    </InfoHint>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {categories.slice(0, 6).map(category => (
                  <div key={category.key} className="flex items-center justify-between gap-3 text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-slate-600">{category.label}</span>
                      <InfoHint title={category.label} width={480}>
                        <CategoryHelp category={category} microPoints={microPoints} />
                      </InfoHint>
                    </div>
                    <span className={`rounded-md px-2 py-1 text-xs font-medium ${category.enabled === false ? 'bg-slate-100 text-slate-500' : 'bg-sky-50 text-sky-700'}`}>
                      {category.enabled === false ? 'Off' : `${category.weight}%`}
                    </span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel icon={PiLightningLight} title="Workflow Notes" subtitle="How this configuration is applied.">
              <div className="space-y-3 text-sm leading-6 text-slate-600">
                <p>Preferences are stored on the employer profile and used by jobs, publishing, and match review defaults.</p>
                <p>Advanced weights are employer-specific and written to the backend weight configuration when that table is available.</p>
                <p>Micro-point overrides are applied when job requirements are extracted and when deterministic matching reads current job micro requirements.</p>
              </div>
            </Panel>
          </div>
        </div>

        {hasChanges && (
          <div className="fixed bottom-4 left-4 right-4 z-30 mx-auto flex max-w-7xl items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-[0_14px_36px_rgba(15,23,42,0.12)]">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <PiLightningLight size={13} className="text-sky-700" />
              Unsaved matching preference changes
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-50"
            >
              {saving ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiFloppyDiskLight size={12} />}
              Save
            </button>
          </div>
        )}
      </div>

      <AdvancedSettingsModal
        open={advancedOpen}
        draft={draftConfig}
        saving={savingAdvanced}
        onClose={() => {
          if (!savingAdvanced) setAdvancedOpen(false);
        }}
        onSave={handleAdvancedSave}
        onReset={handleAdvancedReset}
        onDraftChange={setDraftConfig}
      />
    </div>
  );
};

export default ConfigPage;
